import { UserRoles } from 'constants/auth';
import { createContext, ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APPLICATION_ROUTES } from 'constants/routes';
import jwtDecode from 'jwt-decode';
import { IRole } from 'modules/users/types';
import { authLogoutRequest, authMeRequest, AuthMeResponse } from '../api';
import { refreshTokenRequest } from '../../../api/refreshToken';
import Loader from '../../../shared/components/ui-component/Loader';

export interface ICurrentUser {
    sub: string;
    roles: IRole[];
    franchise_partner_id?: string;
    /** From GET /auth/me when available; JWT decode path may omit this. */
    firstName?: string;
}

function meResponseToCurrentUser(me: AuthMeResponse): ICurrentUser {
    return {
        sub: me.email,
        roles: me.roles,
        franchise_partner_id: me.franchise_partner_id ?? undefined,
        firstName: me.first_name?.trim() || undefined
    };
}

/**
 * Represents the shape of our authentication context
 */
export interface AuthContextProps {
    getAuthToken: () => string | undefined;
    getRefreshToken: () => string | undefined;
    setTokens: (accessToken: string | undefined, refreshToken: string | undefined) => void;
    /** Set currentUser from GET /auth/me (cookie flow after login). Returns user on success. */
    setSessionFromMe: () => Promise<ICurrentUser | undefined>;
    currentUser?: ICurrentUser;
    logout: () => void;
    isOwner: boolean;
    isAdmin: boolean;
}

/**
 * Initialize the context with empty function bodies.
 */
export const AuthContext = createContext<AuthContextProps>({
    getAuthToken: () => undefined,
    getRefreshToken: () => undefined,
    setTokens: () => {},
    setSessionFromMe: async () => undefined,
    currentUser: undefined,
    logout: () => undefined,
    isOwner: false,
    isAdmin: false
});

/**
 * Our AuthProvider component will wrap any components that need to have
 * access to, or the ability to modify, our authentication state (tokens).
 */
export const AuthProvider = ({ children }: { children: ReactElement }): ReactElement => {
    const navigate = useNavigate();
    const [accessToken, setAccessToken] = useState<string | undefined>(() => localStorage.getItem('access_token') ?? undefined);
    const [refreshToken, setRefreshToken] = useState<string | undefined>(() => localStorage.getItem('refresh_token') ?? undefined);
    const [currentUser, setCurrentUser] = useState<ICurrentUser | undefined>(() =>
        localStorage.getItem('currentUser') ? (JSON.parse(localStorage.getItem('currentUser') as string) as ICurrentUser) : undefined
    );
    const [isBootstrapDone, setIsBootstrapDone] = useState<boolean>(false);

    const isOwner = currentUser?.roles.some((role) => role.name === UserRoles.owner) || false;
    const isAdmin = currentUser?.roles.some((role) => role.name === UserRoles.admin) || false;

    const getAuthToken = useCallback(() => accessToken, [accessToken]);
    const getRefreshToken = useCallback(() => refreshToken, [refreshToken]);

    /** Set currentUser from GET /auth/me (used after login when using cookie auth). */
    const setSessionFromMe = useCallback(async () => {
        try {
            const me = await authMeRequest();
            const user = meResponseToCurrentUser(me);
            setCurrentUser(user);
            return user;
        } catch (error) {
            console.error('Failed to fetch /auth/me', error);
            return undefined;
        }
    }, []);

    /**
     * Set both tokens (legacy: localStorage + Bearer). Also sets currentUser from access token.
     */
    const setTokens = useCallback((newAccessToken: string | undefined, newRefreshToken: string | undefined) => {
        localStorage.setItem('access_token', newAccessToken || '');
        localStorage.setItem('refresh_token', newRefreshToken || '');
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);
        try {
            if (newAccessToken) {
                const userData: ICurrentUser = jwtDecode(newAccessToken);
                setCurrentUser(userData);
                localStorage.setItem('currentUser', JSON.stringify(userData));
            }
        } catch (error) {
            console.error('Error occurred while decoding the JWT accessToken', error);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await authLogoutRequest();
        } catch {
            // Ignore; clear local state anyway
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('currentUser');
        setAccessToken(undefined);
        setRefreshToken(undefined);
        setCurrentUser(undefined);
        navigate(APPLICATION_ROUTES.auth.login);
    }, [navigate]);

    /** Bootstrap: try /auth/me (cookie), then legacy localStorage. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const me = await authMeRequest();
                if (!cancelled) {
                    setCurrentUser(meResponseToCurrentUser(me));
                    setIsBootstrapDone(true);
                    return;
                }
            } catch {
                // 401 or network error: try legacy
            }
            const storedAccess = localStorage.getItem('access_token');
            const storedRefresh = localStorage.getItem('refresh_token');
            if (storedAccess && storedRefresh && !cancelled) {
                setAccessToken(storedAccess);
                setRefreshToken(storedRefresh);
                try {
                    const userData: ICurrentUser = jwtDecode(storedAccess);
                    setCurrentUser(userData);
                } catch {
                    setCurrentUser(undefined);
                }
                try {
                    const { data } = await refreshTokenRequest(storedRefresh);
                    if (!cancelled) {
                        setTokens(data.access_token, data.refresh_token);
                    }
                } catch {
                    if (!cancelled) {
                        setIsBootstrapDone(true);
                        logout();
                    }
                }
            }
            if (!cancelled) setIsBootstrapDone(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [logout, setTokens]);

    return (
        <AuthContext.Provider value={{ getAuthToken, getRefreshToken, setTokens, setSessionFromMe, logout, currentUser, isOwner, isAdmin }}>
            {!isBootstrapDone ? <Loader /> : children}
        </AuthContext.Provider>
    );
};
